import React, {useState, useEffect, useRef, useMemo} from "react";
import Editor from "@monaco-editor/react";
import {patchDrfRequest, postDrfRequest, deleteDrfRequest, postFormDataRequest, getDrfRequest, buildUrl, getCsrfToken, getYesterdayDate, parseRelativeDate, object_is_empty} from "./utils";
import {getSubmitButtonOptions, RJSFSchema, SubmitButtonProps} from '@rjsf/utils';
import {Button, FormControl, InputGroup, Table, Modal, Card, Row, Col, Accordion, Dropdown, ButtonGroup} from "react-bootstrap";
import {
    Back,
    CheckLg,
    ChevronDown,
    FolderPlus,
    EyeFill,
    Trash3Fill,
    ArrowCounterclockwise,
    PlusLg,
    PencilSquare,
    XLg,
    ChevronRight,
    HouseDoorFill,
    FileEarmarkTextFill,
    FiletypeHtml,
    FiletypeTxt,
    ArrowClockwise,
    CheckCircleFill,
    ExclamationTriangleFill,
    GearFill,
    JournalBookmarkFill,
    Files,
    TypeBold,
    TypeItalic,
    TypeUnderline,
    ListUl,
    ListOl,
    Link45deg,
    Upload,
    Download,
    PersonFill
} from "react-bootstrap-icons";
import { usePromiseTracker } from "react-promise-tracker";
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { getIcon } from 'pretty-file-icons';

// Load all icons from pretty-file-icons
const iconContext = require.context('pretty-file-icons/svg', false, /\.svg$/);
const prettyIcons = {};
iconContext.keys().forEach(key => {
    const name = key.replace('./', '').replace('.svg', '');
    prettyIcons[name] = iconContext(key);
});

const getPrettyIconSrc = (filename, mimetype) => {
    let iconName = getIcon(filename);
    if ((iconName === 'unknown' || !filename) && mimetype) {
        if (mimetype.startsWith('image/')) iconName = 'jpg';
        else if (mimetype.startsWith('audio/')) iconName = 'audio';
        else if (mimetype.startsWith('video/')) iconName = 'video';
        else if (mimetype === 'application/pdf') iconName = 'pdf';
        else if (mimetype === 'application/zip' || mimetype.includes('archive')) iconName = 'archive';
        else if (mimetype === 'text/plain' || mimetype === 'text/markdown') iconName = 'txt';
        else if (mimetype.includes('json')) iconName = 'json';
        else if (mimetype.includes('xml')) iconName = 'xml';
    }
    return prettyIcons[iconName] || prettyIcons['unknown'];
};



const buildSchemaTree = (schemas, parentPk = null) => {
    return schemas
        .filter(s => {
            const sParentPk = (s.parent && typeof s.parent === 'object') ? s.parent.pk : s.parent;
            return (sParentPk === parentPk) || (!parentPk && !sParentPk);
        })
        .map(s => ({
            ...s,
            children: buildSchemaTree(schemas, s.pk)
        }));
};

const SchemaTreeItem = ({ schema, currentPk, pushSchemaPath, level = 0, isLast = false, parentLastFlags = [] }) => {
    const isActive = schema.pk === currentPk;
    const hasChildren = schema.children && schema.children.length > 0;
    
    // Connectors: │  ├─ └─
    const prefix = parentLastFlags.map(isLastParent => isLastParent ? '    ' : '│   ').join('');
    const connector = level === 0 ? '' : (isLast ? '└── ' : '├── ');

    return (
        <div className="d-flex flex-column" style={{ lineHeight: '1.2' }}>
            <div className="d-flex align-items-center">
                <span className="text-muted small" style={{ fontFamily: 'monospace', whiteSpace: 'pre', opacity: 0.6, fontSize: '0.7rem' }}>
                    {prefix}{connector}
                </span>
                <button 
                    className={`btn btn-link p-0 text-decoration-none d-flex align-items-center ${isActive ? 'fw-bold text-primary underline-active' : 'text-muted'}`}
                    style={{ fontSize: '0.8rem', textAlign: 'left' }}
                    onClick={() => pushSchemaPath(schema.pk)}
                    title={schema.headline}
                >
                    <StackedSchemaIcon size={14} className={`me-1 ${isActive ? 'text-primary' : 'text-secondary'}`} />
                    <span className="text-truncate" style={{maxWidth: '200px'}}>{schema.headline}</span>
                </button>
            </div>
            {hasChildren && schema.children.map((child, idx) => (
                <SchemaTreeItem 
                    key={child.pk}
                    schema={child}
                    currentPk={currentPk}
                    pushSchemaPath={pushSchemaPath}
                    level={level + 1}
                    isLast={idx === schema.children.length - 1}
                    parentLastFlags={level === 0 ? [] : [...parentLastFlags, isLast]}
                />
            ))}
        </div>
    );
};

export const Breadcrumbs = ({ stack, onGoTo, pushSchemaPath, schemas = [] }) => {
    const displayStack = stack;
    const currentView = stack[stack.length - 1];
    
    let activeSchemaPk = null;
    if (currentView.type === 'schema_records') activeSchemaPk = currentView.props?.pk;
    else if (currentView.type === 'record') activeSchemaPk = currentView.props?.schema;
    else if (currentView.type === 'schema') activeSchemaPk = currentView.props?.pk;

    const totalSchemas = schemas.length;
    const showTree = totalSchemas > 0 && totalSchemas <= 30 && activeSchemaPk !== null;

    if (showTree) {
        const treeData = buildSchemaTree(schemas, null);
        return (
            <div className="schema-hierarchy-tree mb-3 p-3 bg-light rounded border shadow-sm" style={{ minWidth: '300px' }}>
                <div className="d-flex align-items-center mb-2 pb-2 border-bottom">
                    <button 
                        className="btn btn-link p-0 text-decoration-none text-muted d-flex align-items-center"
                        style={{ fontSize: '0.9rem' }}
                        onClick={() => onGoTo(0)}
                    >
                        <HouseDoorFill size={18} className="me-2" />
                        <span className="fw-bold">Vorlagen-Hierarchie</span>
                    </button>
                    {(currentView.type === 'settings' || (currentView.type === 'schema' && currentView.title === 'Einstellungen')) && (
                        <div className="ms-auto d-flex align-items-center text-primary">
                            <GearFill size={16} className="me-1" />
                            <span className="small fw-bold">Einstellungen</span>
                        </div>
                    )}
                    {currentView.type === 'record' && (
                        <div className="ms-auto d-flex align-items-center text-orange">
                            <FileEarmarkTextFill size={16} className="me-1" />
                            <span className="small fw-bold text-truncate" style={{maxWidth: '150px'}}>{currentView.title}</span>
                        </div>
                    )}
                </div>
                <div className="schema-tree-nodes overflow-auto" style={{maxHeight: '400px'}}>
                    {treeData.map((schema, idx) => (
                        <SchemaTreeItem 
                            key={schema.pk}
                            schema={schema}
                            currentPk={activeSchemaPk}
                            pushSchemaPath={pushSchemaPath}
                            isLast={idx === treeData.length - 1}
                        />
                    ))}
                </div>
                <style>
                    {`
                        .underline-active { text-decoration: underline !important; }
                        .schema-hierarchy-tree .btn-link:hover { background: rgba(0,0,0,0.05); border-radius: 4px; padding-right: 5px !important; }
                    `}
                </style>
            </div>
        );
    }

    return (
        <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0 bg-transparent p-0">
                {displayStack.map((view, index) => {
                    const isLast = index === displayStack.length - 1;
                    
                    let icon = null;
                    let title = view.title;
                    if (view.type === 'home') {
                        icon = <HouseDoorFill className="me-1 mb-1" />;
                    } else if (view.type === 'schema' || view.type === 'settings') {
                        icon = <GearFill size={16} className="me-1 mb-1 text-primary" />;
                        title = "Einstellungen";
                    } else if (view.type === 'schema_records') {
                        icon = <StackedSchemaIcon size={16} className="me-1 mb-1 text-primary" />;
                    } else if (view.type === 'record') {
                        icon = <FileEarmarkTextFill size={16} className="me-1 mb-1 text-orange" />;
                    }

                    return (
                        <li key={index} className={`breadcrumb-item ${isLast ? 'active' : ''}`} aria-current={isLast ? 'page' : undefined}>
                            {!isLast ? (
                                <button 
                                    className="btn btn-link p-0 text-decoration-none text-muted d-flex align-items-center fw-bold" 
                                    onClick={() => onGoTo(index)}
                                >
                                    {icon}
                                    {title}
                                </button>
                            ) : (
                                <span className="text-dark d-flex align-items-center fw-bold">
                                    {icon}
                                    {title}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};


export const LoadingSpinner = () => {
    const { promiseInProgress } = usePromiseTracker();

    if (!promiseInProgress) return null;

    return (
        <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '10px 15px',
            borderRadius: '50px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            border: '1px solid #eee'
        }}>
            <ArrowClockwise className="spinner-border-anim" size={20} style={{ color: '#0d6efd' }} />
            <span className="small fw-bold text-dark">Wird geladen...</span>
            <style>
                {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    .spinner-border-anim {
                        animation: spin 1s linear infinite;
                    }
                `}
            </style>
        </div>
    );
};


export const ToastNotification = ({ show, message, variant, onHide }) => {
    if (!show) return null;
    const Icon = variant === 'success' ? CheckCircleFill : ExclamationTriangleFill;
    
    return (
        <div style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 9999,
            minWidth: '250px'
        }}>
            <div className={`p-3 rounded shadow-lg d-flex align-items-center gap-4 text-white border-0 bg-${variant === 'success' ? 'success' : 'danger'}`}>
                <Icon size={24} />
                <div className="flex-grow-1 fw-medium">{message}</div>
                <button type="button" className="btn-close btn-close-white" onClick={onHide}></button>
            </div>
        </div>
    );
};

export const StackedSchemaIcon = ({size = 32, className = "text-primary"}) => {
    const offset = Math.max(2, Math.floor(size / 4));
    return (
        <div style={{position: 'relative', width: size + offset * 2, height: size + offset * 2, display: 'inline-block', verticalAlign: 'middle'}}>
            <FileEarmarkTextFill size={size} style={{position: 'absolute', top: offset * 2, left: offset * 2, opacity: 0.3}} className={`${className} file-icon-svg`} />
            <FileEarmarkTextFill size={size} style={{position: 'absolute', top: offset, left: offset, opacity: 0.6}} className={`${className} file-icon-svg`} />
            <FileEarmarkTextFill size={size} style={{position: 'absolute', top: 0, left: 0, opacity: 1}} className={`${className} file-icon-svg`} />
        </div>
    );
};


export const PrettyIcon = ({filename, mimetype, size = 32, hoverThumbnail = null, ...props}) => {
    const [hover, setHover] = useState(false);
    
    const iconSrc = getPrettyIconSrc(filename, mimetype);
    const icon = <img src={iconSrc} alt="file icon" style={{width: size, height: size}} className="file-icon-svg" {...props} />;

    if (!hoverThumbnail) return icon;

    return (
        <div 
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{position: 'relative', display: 'inline-block'}}
        >
            {icon}
            {hover && (
                <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1000,
                    marginBottom: '5px'
                }}>
                    <PdfThumbnail 
                        thumbnailUrl={hoverThumbnail} 
                        fileUrl={hoverThumbnail.replace('/thumbnail?path=', '')} 
                        size="150px" 
                        thumbnailWidth={300}
                    />
                </div>
            )}
        </div>
    );
};


export const StackedPdfThumbnail = ({pdfThumbnails, size = '80px', fullWidth = false, onClick = null}) => {
    const displayThumbnails = pdfThumbnails.slice(0, 3).reverse(); // Render bottom to top
    const maxOffset = (displayThumbnails.length - 1) * 8;
    
    return (
        <div 
            onClick={onClick}
            style={{
                position: 'relative',
                width: fullWidth ? '100%' : size,
                aspectRatio: '1 / 1.414',
                cursor: onClick ? 'pointer' : 'default',
                display: fullWidth ? 'block' : 'inline-block',
            }}
        >
            {displayThumbnails.map((pdf, index) => {
                const total = displayThumbnails.length;
                const reverseIndex = total - 1 - index; // 0 for top, 1 for mid, 2 for bottom
                const offset = reverseIndex * 8; 
                return (
                    <div
                        key={index}
                        style={{
                            position: 'absolute',
                            top: `${offset}px`,
                            left: `${offset}px`,
                            width: `calc(100% - ${maxOffset}px)`,
                            height: `calc(100% - ${maxOffset}px)`,
                            zIndex: index,
                            borderRadius: '3px',
                            backgroundColor: 'white',
                            border: '1px solid #dee2e6',
                            overflow: 'hidden',
                        }}
                    >
                        <img
                            src={pdf.thumbnail_url}
                            alt={`PDF Thumbnail ${index}`}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block'
                            }}
                        />
                        {index === total - 1 && (
                            <div style={{
                                position: 'absolute',
                                bottom: '5px',
                                left: '5px',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                borderRadius: '50%',
                                padding: '4px',
                                display: 'flex',
                                border: '1px solid #dee2e6'
                            }}>
                                <PrettyIcon mimetype="application/pdf" size={32} />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};


export const PdfThumbnail = ({thumbnailUrl, fileUrl = null, size = '80px', fullWidth = false, onClick = null, thumbnailWidth = null}) => {
    const [hover, setHover] = useState(false);
    
    const finalThumbnailUrl = thumbnailWidth ? `${thumbnailUrl}${thumbnailUrl.includes('?') ? '&' : '?'}width=${thumbnailWidth}` : thumbnailUrl;

    const Component = (fileUrl && !onClick) ? 'a' : 'div';
    const linkProps = (fileUrl && !onClick) ? {
        href: fileUrl,
        download: '',
        target: '_blank',
        rel: 'noopener noreferrer',
        onClick: (e) => e.stopPropagation()
    } : {
        onClick: (e) => {
            if (onClick) {
                e.stopPropagation();
                onClick(e);
            }
        }
    };

    return (
        <Component 
            {...linkProps}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                position: 'relative',
                width: fullWidth ? '100%' : size,
                height: 'auto',
                display: fullWidth ? 'block' : 'inline-block',
                borderRadius: '6px',
                overflow: 'hidden',
                cursor: (onClick || fileUrl) ? 'pointer' : 'default',
                transition: 'all 0.2s ease-in-out',
                border: hover ? '1px dashed #dee2e6' : '1px solid #dee2e6',
                textDecoration: 'none'
            }}
        >
            <img
                src={finalThumbnailUrl}
                alt="PDF Thumbnail"
                style={{
                    width: '100%',
                    display: 'block',
                    transition: 'filter 0.2s',
                    filter: hover ? 'brightness(0.9)' : 'none'
                }}
            />
            <div 
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <PrettyIcon mimetype="application/pdf" size={72} title="PDF herunterladen" />
            </div>
        </Component>
    );
};


export const FileEditorModal = ({show, onHide, filePath, refreshData, showNotification}) => {
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (show && filePath) {
            setLoading(true);
            const url = buildUrl("/api/file/", {path: filePath});
            getDrfRequest(url, (data) => {
                setContent(data.content);
                setLoading(false);
            }, (error) => {
                console.error("Error fetching file content:", error);
                setLoading(false);
                if (showNotification) showNotification('Fehler beim Laden der Datei', 'danger');
            });
        }
    }, [show, filePath]);

    const handleSave = () => {
        const url = buildUrl("/api/file/");
        const data = {
            path: filePath,
            content: content
        };
        postDrfRequest(url, data, (response) => {
            console.log("File saved successfully", response);
            if (showNotification) showNotification('Datei erfolgreich gespeichert');
            if (refreshData) refreshData();
            onHide();
        }, (error) => {
            console.error("Error saving file:", error);
            if (showNotification) showNotification('Fehler beim Speichern der Datei', 'danger');
        });
    };

    const getLanguage = (path) => {
        if (!path) return 'plaintext';
        const cleanPath = path.split('?')[0].split('#')[0].toLowerCase();
        if (cleanPath.endsWith('.tex')) return 'latex';
        if (cleanPath.endsWith('.json')) return 'json';
        if (cleanPath.endsWith('.js')) return 'javascript';
        if (cleanPath.endsWith('.css')) return 'css';
        if (cleanPath.endsWith('.scss')) return 'scss';
        if (cleanPath.endsWith('.html')) return 'html';
        if (cleanPath.endsWith('.md')) return 'markdown';
        return 'plaintext';
    };

    const language = getLanguage(filePath);
    const displayTitle = filePath ? filePath.split('/').pop().split('?')[0] : "";

    return (
        <Modal show={show} onHide={onHide} size="xl" animation={false}>
            <Modal.Header closeButton>
                <Modal.Title className="text-truncate">Datei bearbeiten: {displayTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: 0, backgroundColor: '#1e1e1e' }}>
                {loading ? (
                    <div className="p-5 text-center text-light">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <Editor
                        height="75vh"
                        theme="vs-dark"
                        language={language}
                        value={content}
                        onChange={(value) => setContent(value || "")}
                        options={{
                            minimap: { enabled: false },
                            fontSize: 14,
                            scrollBeyondLastLine: false,
                            wordWrap: "on",
                            automaticLayout: true,
                            tabSize: 4,
                            padding: { top: 10, bottom: 10 }
                        }}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Abbrechen
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={loading}>
                    Speichern
                </Button>
            </Modal.Footer>
        </Modal>
    );
};


export const FileList = ({file_set, refreshData, compact, grid, show, onToggle, showNotification, uploadUrl, fullWidth = false, allowDelete = true, hideFilename = false}) => {
    const [files, setFiles] = useState(file_set);
    const [showEditor, setShowEditor] = useState(false);
    const [currentFile, setCurrentFile] = useState(null);
    const [internalIsVisible, setInternalIsVisible] = useState(true);

    const isVisible = show !== undefined ? show : internalIsVisible;

    useEffect(() => {
        setFiles(file_set);
    }, [file_set]);

    const handleDelete = (file) => {
        deleteDrfRequest(file.url, () => {
            if (showNotification) showNotification('Datei erfolgreich gelöscht');
            if (refreshData) {
                refreshData();
            } else {
                setFiles((prevFiles) => prevFiles.filter((prev_file) => prev_file.pk !== file.pk));
            }
        }, (error) => {
            console.log(error);
            if (showNotification) showNotification('Fehler beim Löschen der Datei', 'danger');
        });
    };

    const handleCopy = (file) => {
        if (!uploadUrl) return;
        const copyUrl = uploadUrl.replace('upload_file/', 'copy_file/');
        postDrfRequest(copyUrl, { file_pk: file.pk }, (response) => {
            if (showNotification) showNotification('Datei erfolgreich kopiert');
            if (refreshData) refreshData();
        }, (error) => {
            console.error(error);
            if (showNotification) showNotification('Fehler beim Kopieren der Datei', 'danger');
        });
    };

    if (grid) {
        return (
            <div className="d-flex flex-row flex-wrap gap-4 align-items-start py-2">
                {isVisible && files.map((file) => (
                    <div key={file.pk} className={`d-flex flex-column ${fullWidth ? 'w-100' : 'align-items-center'}`} style={fullWidth ? {} : {width: '100px'}}>
                        <div className="mb-2 w-100">
                            {file.thumbnail_url ? (
                                <PdfThumbnail 
                                    thumbnailUrl={file.thumbnail_url} 
                                    fileUrl={file.file} 
                                    size="100px" 
                                    fullWidth={fullWidth} 
                                    thumbnailWidth={fullWidth ? 1024 : 300}
                                />
                            ) : (
                                <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{width: fullWidth ? '100%' : '100px', height: fullWidth ? 'auto' : '140px'}}>
                                    <PrettyIcon filename={file.basename} mimetype={file.mimetype} size={48} />
                                </div>
                            )}
                        </div>
                        <div className="d-flex justify-content-between align-items-center w-100 px-1">
                            {!hideFilename && (
                                <div className="text-truncate small text-muted flex-grow-1 me-2" title={file.basename}>
                                    {file.basename}
                                    {file.is_inherited && <span className="ms-1 badge bg-light text-dark border small" style={{fontSize: '0.6rem'}}>vererbt</span>}
                                </div>
                            )}
                            <div className="d-flex gap-1">
                                {file.mimetype !== 'application/pdf' && (
                                    <button 
                                        className={`btn btn-link btn-sm p-0 border-0 ${file.is_inherited ? 'text-info' : 'text-secondary'}`}
                                        title={file.is_inherited ? 'Kopieren & Bearbeiten' : 'Bearbeiten'}
                                        onClick={() => {
                                            if (file.is_inherited) {
                                                handleCopy(file);
                                            } else {
                                                setCurrentFile(file.file);
                                                setShowEditor(true);
                                            }
                                        }}
                                    >
                                        <PencilSquare size={32}/>
                                    </button>
                                )}
                                {allowDelete && !file.is_inherited && (
                                    <button className="btn btn-link btn-sm text-danger p-0 border-0" onClick={() => handleDelete(file)}>
                                        <Trash3Fill size={32}/>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
                {uploadUrl && (
                    <div style={{width: '100px', height: '140px'}} className="d-flex align-items-center justify-content-center border rounded border-dashed">
                        <UploadFile 
                            url={uploadUrl} 
                            refreshData={refreshData} 
                            showNotification={showNotification}
                            className="w-100 h-100 d-flex align-items-center justify-content-center"
                        />
                    </div>
                )}
                <FileEditorModal
                    show={showEditor}
                    onHide={() => setShowEditor(false)}
                    filePath={currentFile}
                    refreshData={refreshData}
                    showNotification={showNotification}
                />
            </div>
        );
    }

    return (
        <div className={`${(compact || fullWidth) ? 'w-100' : 'w-50'} mb-3`}>
            {isVisible && (
                <>
                    <Table hover size="sm" className="align-middle mt-2">
                        {!compact && (
                            <thead className="table-light">
                            <tr>
                                {!hideFilename && <th className="ps-3">Name</th>}
                                <th>Typ</th>
                                <th>Größe</th>
                                <th>Datum</th>
                                <th className="text-end pe-3">Aktionen</th>
                            </tr>
                            </thead>
                        )}
                        <tbody>
                        {files.map((file) => (
                            <tr key={file.pk} className={file.is_inherited ? 'table-light' : ''}>
                                {!hideFilename && (
                                    <td className="ps-3 fw-medium">
                                        <div className="text-truncate d-flex align-items-center" style={{maxWidth: compact ? '120px' : '300px'}} title={file.basename}>
                                            {file.basename}
                                            {file.is_inherited && <span className="ms-2 badge bg-light text-dark border fw-normal" style={{fontSize: '0.7rem'}}>vererbt</span>}
                                        </div>
                                    </td>
                                )}
                                <td>
                                    <a className="btn btn-outline-primary btn-sm p-0 px-2 border-0" href={file.file} title={file.mimetype}>
                                        <PrettyIcon filename={file.basename} mimetype={file.mimetype} hoverThumbnail={file.thumbnail_url} />
                                        {!compact && <small className="ms-1">{file.mimetype.split('/')[1]}</small>}
                                    </a>
                                </td>
                                {!compact && (
                                    <>
                                        <td><small className="text-muted">{file.human_size}</small></td>
                                        <td><small className="text-muted">{file.human_date}</small></td>
                                    </>
                                )}
                                <td className="text-end pe-3">
                                    <div className="d-flex justify-content-end gap-1">
                                        {file.mimetype !== 'application/pdf' && (
                                            <button
                                                className={`btn btn-link btn-sm p-0 ${file.is_inherited ? 'text-info' : 'text-secondary'}`}
                                                title={file.is_inherited ? 'Kopieren & Bearbeiten' : 'Bearbeiten'}
                                                onClick={() => {
                                                    if (file.is_inherited) {
                                                        handleCopy(file);
                                                    } else {
                                                        setCurrentFile(file.file);
                                                        setShowEditor(true);
                                                    }
                                                }}
                                            >
                                                <PencilSquare/>
                                            </button>
                                        )}
                                        {allowDelete && !file.is_inherited && (
                                            <button className="btn btn-link btn-sm text-secondary p-0" onClick={() => handleDelete(file)}>
                                                <Trash3Fill/>
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!compact && uploadUrl && (
                            <tr>
                                <td colSpan={5} className="ps-3 py-2 bg-light">
                                    <UploadFile url={uploadUrl} refreshData={refreshData} showNotification={showNotification} />
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </Table>
                </>
            )}

            {currentFile && (
                <FileEditorModal
                    show={showEditor}
                    onHide={() => setShowEditor(false)}
                    filePath={currentFile}
                    refreshData={refreshData}
                    showNotification={showNotification}
                />
            )}
        </div>
    );
};


/*
 * used by the JsonSchemaForm callback.
 * It uses the uiSchema to determine if the button should be rendered.
 */
export const JsonFormSubmitButton = (props) => {
    const {uiSchema} = props;
    const {norender, props: buttonProps} = getSubmitButtonOptions(uiSchema);

    if (norender) {
        return <button type="submit" style={{display: 'none'}} />;
    }

    const isGreen = uiSchema?.["ui:submitButtonOptions"]?.variant === 'green';

    return (
        <div className="my-4">
            <Button
                variant={isGreen ? 'green' : 'primary'}
                type="submit"
                {...buttonProps}
                className={`w-100 py-3 fw-bold shadow-lg d-flex align-items-center justify-content-center gap-2 rounded-pill border-0 transition-all ${isGreen ? 'btn-green' : ''}`}
                style={{
                    letterSpacing: '1px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    backgroundColor: isGreen ? '#198754' : '#0d6efd',
                    backgroundImage: isGreen
                        ? 'linear-gradient(135deg, #198754 0%, #10b981 100%)'
                        : 'linear-gradient(135deg, #0d6efd 0%, #0043ce 100%)',
                    textTransform: 'uppercase',
                    fontSize: '0.9rem',
                    color: 'white'
                }}
            >
                <CheckLg size={22}/>
                Speichern
            </Button>
            <style>
                {`
                    .btn-primary.transition-all:hover, .btn-green.transition-all:hover {
                        transform: translateY(-3px) scale(1.01) !important;
                        border: 2px dashed white !important;
                        filter: brightness(1.1);
                        color: white !important;
                        cursor: pointer;
                    }
                    .btn-primary.transition-all:active, .btn-green.transition-all:active {
                        transform: translateY(-1px) scale(0.99) !important;
                    }
                `}
            </style>
        </div>
    );
}


/**
 * A custom widget that renders a text input with an HTML5 datalist for autocomplete.
 */
export const DatalistWidget = (props) => {
    const {
        id,
        placeholder,
        required,
        readonly,
        disabled,
        value,
        onChange,
        onBlur,
        onFocus,
        options,
    } = props;

    const list = options.list || [];
    const datalistId = `datalist-${id}`;

    return (
        <div className="datalist-widget-container">
            <InputGroup>
                <FormControl
                    id={id}
                    list={datalistId}
                    value={value || ""}
                    placeholder={placeholder}
                    required={required}
                    disabled={disabled}
                    readOnly={readonly}
                    autoComplete="off"
                    onChange={(event) => onChange(event.target.value)}
                    onBlur={onBlur && ((event) => onBlur(id, event.target.value))}
                    onFocus={onFocus && ((event) => onFocus(id, event.target.value))}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            // Standard datalist might handle Enter to select,
                            // but we want to move to next field too.
                            setTimeout(() => {
                                const form = e.target.form;
                                if (form) {
                                    const elements = Array.from(form.elements);
                                    const index = elements.indexOf(e.target);
                                    if (index > -1) {
                                        const nextElement = elements.slice(index + 1).find(el =>
                                            (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') &&
                                            !el.disabled && el.type !== 'hidden'
                                        );
                                        if (nextElement) nextElement.focus();
                                    }
                                }
                            }, 10);
                        }
                    }}
                />
                {value && !readonly && !disabled && (
                    <Button
                        variant="link"
                        className="combobox-clear p-0 px-2"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onChange("");
                        }}
                    >
                        <XLg size={12} />
                    </Button>
                )}
            </InputGroup>
            {list.length > 0 && (
                <datalist id={datalistId}>
                    {list.map((item, index) => (
                        <option key={`${item}-${index}`} value={item} />
                    ))}
                </datalist>
            )}
        </div>
    );
};


/**
 * A more advanced autocomplete widget that behaves like a selector but allows custom values.
 */
export const SimpleCombobox = ({ id, value, onChange, list = [], placeholder, size, disabled, readonly, required, onFocus, onBlur }) => {
    const [show, setShow] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [isManualTyping, setIsManualTyping] = useState(false);

    const isObjectList = list.length > 0 && typeof list[0] === 'object';

    // Find the current item to display its label
    const selectedItem = isObjectList
        ? list.find(item => {
            const itemValue = item.pk !== undefined ? item.pk : item.id;
            return String(itemValue) === String(value);
        })
        : null;

    const displayValue = show ? filterText : (isObjectList ? (selectedItem?.label || "") : (value || ""));

    const filteredOptions = isObjectList
        ? list.filter(item => !isManualTyping || (item.label || "").toLowerCase().includes(filterText.toLowerCase()))
        : list.filter(opt => opt && typeof opt === 'string' && (!isManualTyping || opt.toLowerCase().includes(filterText.toLowerCase())));

    return (
        <Dropdown
            show={show}
            onToggle={(nextShow) => {
                setShow(nextShow);
                if (nextShow) {
                    setFilterText(isObjectList ? (selectedItem?.label || "") : (value || ""));
                    setIsManualTyping(false);
                }
            }}
            className="combobox-widget-container w-100"
        >
            <InputGroup size={size} className="w-100">
                <FormControl
                    id={id}
                    value={displayValue}
                    placeholder={placeholder}
                    required={required}
                    disabled={disabled}
                    readOnly={readonly}
                    autoComplete="off"
                    onChange={(event) => {
                        const val = event.target.value;
                        setFilterText(val);
                        setIsManualTyping(true);
                        if (!isObjectList) {
                            onChange(val);
                        } else {
                            const exactMatch = list.find(item =>
                                String(item.label).toLowerCase() === val.toLowerCase()
                            );
                            if (exactMatch) {
                                const itemValue = exactMatch.pk !== undefined ? exactMatch.pk : exactMatch.id;
                                onChange(itemValue);
                            }
                        }
                        if (!show) setShow(true);
                    }}
                    onFocus={(e) => {
                        setShow(true);
                        // Preserve current value/label so it doesn't appear to disappear
                        setFilterText(isObjectList ? (selectedItem?.label || "") : (value || ""));
                        setIsManualTyping(false);
                        if (onFocus) onFocus(id, e.target.value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            if (show) {
                                e.preventDefault();
                                const exactMatch = filteredOptions.find(opt => {
                                    const label = isObjectList ? opt.label : opt;
                                    return String(label).toLowerCase() === filterText.toLowerCase();
                                });

                                if (exactMatch) {
                                    const itemValue = isObjectList ? (exactMatch.pk !== undefined ? exactMatch.pk : exactMatch.id) : exactMatch;
                                    onChange(itemValue);
                                } else if (isObjectList && isManualTyping && filteredOptions.length > 0) {
                                    // For object lists, if typing, pick the first match even if not exact
                                    const firstItem = filteredOptions[0];
                                    const itemValue = firstItem.pk !== undefined ? firstItem.pk : firstItem.id;
                                    onChange(itemValue);
                                } else if (!isObjectList) {
                                    // For strings, use exactly what was typed (already handled by onChange while typing)
                                    onChange(filterText);
                                }
                                setShow(false);

                                // Focus the next form element only if we just selected something
                                setTimeout(() => {
                                    const form = e.target.form;
                                    if (form) {
                                        const elements = Array.from(form.elements);
                                        const index = elements.indexOf(e.target);
                                        if (index > -1) {
                                            const nextElement = elements.slice(index + 1).find(el =>
                                                (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') &&
                                                !el.disabled && el.type !== 'hidden'
                                            );
                                            if (nextElement) nextElement.focus();
                                        }
                                    }
                                }, 10);
                            }
                        }
                    }}
                    onBlur={(e) => {
                        if (onBlur) onBlur(id, e.target.value);
                    }}
                />
                {value && !readonly && !disabled && (
                    <Button
                        variant="link"
                        className="combobox-clear p-0 px-2"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onChange("");
                            setFilterText("");
                            setIsManualTyping(false);
                        }}
                    >
                        <XLg size={12} />
                    </Button>
                )}
                <Dropdown.Toggle
                    as={Button}
                    className="combobox-toggle"
                    id={id ? `${id}-toggle` : undefined}
                >
                    <ChevronDown size={14} style={{transform: show ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s'}} />
                </Dropdown.Toggle>
            </InputGroup>

            <Dropdown.Menu
                className="w-100 shadow-lg border-0 py-1"
                style={{
                    maxHeight: '250px',
                    overflowY: 'auto',
                    zIndex: 1050
                }}
            >
                {filteredOptions.length > 0 ? (
                    filteredOptions.map((item, index) => {
                        const itemValue = isObjectList ? (item.pk !== undefined ? item.pk : item.id) : item;
                        const itemLabel = isObjectList ? item.label : item;
                        const isActive = itemValue == value;

                        return (
                            <Dropdown.Item
                                key={index}
                                active={isActive}
                                onClick={() => {
                                    onChange(itemValue);
                                    setShow(false);
                                }}
                                className="d-flex align-items-center justify-content-between py-2"
                            >
                                <span className="small">{itemLabel}</span>
                                {isActive && <CheckLg className="ms-2" size={14} />}
                            </Dropdown.Item>
                        );
                    })
                ) : (
                    <div className="px-3 py-2 small text-muted font-italic">
                        {list.length > 0 ? 'Keine Übereinstimmung' : 'Keine Vorschläge verfügbar'}
                    </div>
                )}
                {!isObjectList && filterText && !list.some(opt => String(opt).toLowerCase() === filterText.toLowerCase()) && (
                    <>
                        <Dropdown.Divider />
                        <Dropdown.Item
                            className="small text-primary fw-bold"
                            onClick={() => {
                                onChange(filterText);
                                setShow(false);
                            }}
                        >
                            Neu: "{filterText}"
                        </Dropdown.Item>
                    </>
                )}
            </Dropdown.Menu>
            <style>
                {`
                    .combobox-widget-container .dropdown-toggle::after {
                        display: none;
                    }
                    .combobox-widget-container .dropdown-menu {
                        width: 100% !important;
                        left: 0 !important;
                        right: 0 !important;
                        transform: none !important;
                        top: 100% !important;
                        margin-top: 2px !important;
                        z-index: 1100 !important;
                    }
                    .combobox-widget-container .dropdown-item.active {
                        background-color: #f8f9fa;
                        color: #0d6efd;
                    }
                    .combobox-widget-container .dropdown-item:hover {
                        background-color: #e9ecef;
                    }
                `}
            </style>
        </Dropdown>
    );
};


export const ComboboxWidget = (props) => {
    const { options } = props;
    return <SimpleCombobox {...props} list={options.list || []} />;
};


export const MonacoWidget = (props) => {
    const {
        id,
        value,
        onChange,
        readonly,
        disabled,
        options,
    } = props;

    const language = options?.language || 'plaintext';

    return (
        <div style={{ border: '1px solid #ced4da', borderRadius: '4px', overflow: 'hidden' }}>
            <Editor
                height="300px"
                language={language}
                value={value || ""}
                theme="vs-light"
                onChange={(newValue) => onChange(newValue || "")}
                options={{
                    readOnly: readonly || disabled,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    wordWrap: "on",
                    automaticLayout: true,
                    fontSize: 14,
                    lineNumbers: 'on',
                    folding: true,
                    tabSize: 4,
                }}
            />
        </div>
    );
};


export const HtmlWidget = (props) => {
    const { id, value, onChange, readonly, disabled, label } = props;
    const editorRef = useRef(null);

    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== (value || "")) {
            editorRef.current.innerHTML = value || "";
        }
    }, [value]);

    const execCommand = (command, arg = null) => {
        document.execCommand(command, false, arg);
        if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    };

    const handleInput = () => {
        if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    };

    return (
        <div className="border rounded shadow-sm overflow-hidden">
            {!readonly && !disabled && (
                <div className="border-bottom p-2 bg-light d-flex flex-wrap gap-1">
                    <ButtonGroup size="sm">
                        <Button variant="outline-secondary" className="border-0" onClick={() => execCommand('bold')} title="Fett"><TypeBold /></Button>
                        <Button variant="outline-secondary" className="border-0" onClick={() => execCommand('italic')} title="Kursiv"><TypeItalic /></Button>
                        <Button variant="outline-secondary" className="border-0" onClick={() => execCommand('underline')} title="Unterstrichen"><TypeUnderline /></Button>
                    </ButtonGroup>
                    <ButtonGroup size="sm">
                        <Button variant="outline-secondary" className="border-0" onClick={() => execCommand('insertUnorderedList')} title="Aufzählung"><ListUl /></Button>
                        <Button variant="outline-secondary" className="border-0" onClick={() => execCommand('insertOrderedList')} title="Nummerierung"><ListOl /></Button>
                    </ButtonGroup>
                    <ButtonGroup size="sm">
                        <Button variant="outline-secondary" className="border-0" onClick={() => {
                            const url = prompt("URL eingeben:");
                            if (url) execCommand('createLink', url);
                        }} title="Link"><Link45deg /></Button>
                    </ButtonGroup>
                    <ButtonGroup size="sm">
                        <Button variant="outline-secondary" className="border-0" onClick={() => execCommand('removeFormat')} title="Formatierung entfernen">
                            <ArrowCounterclockwise />
                        </Button>
                    </ButtonGroup>
                </div>
            )}
            <div
                id={id}
                ref={editorRef}
                contentEditable={!readonly && !disabled}
                onInput={handleInput}
                style={{
                    minHeight: '250px',
                    maxHeight: '500px',
                    overflowY: 'auto',
                    padding: '15px',
                    outline: 'none',
                    backgroundColor: (readonly || disabled) ? '#f8f9fa' : 'white',
                    fontSize: '16px',
                    lineHeight: '1.5'
                }}
            />
        </div>
    );
};


/**
 * Recursively applies default values to formData based on the schema.
 * Supports relative date strings like "today", "now", "yesterday" in property.default.
 * Sets date fields to "yesterday" if they are empty and no default is provided.
 * Returns the original data object if no changes were made to maintain stability.
 */
export const applyDefaultValues = (schema, data = {}) => {
    if (!schema || !schema.properties) return data || {};
    let newData = null;

    Object.keys(schema.properties).forEach(key => {
        const property = schema.properties[key];
        const isDateField = property.format === "date" || property["ui:options"]?.inputType === "date";
        const currentValue = data ? data[key] : undefined;

        if (isDateField) {
            const hasNoValue = currentValue === undefined || currentValue === null || currentValue === "";
            const valueToParse = hasNoValue ? (property.default || 'yesterday') : currentValue;
            const parsedValue = parseRelativeDate(valueToParse);

            if (parsedValue !== currentValue) {
                if (!newData) newData = { ...(data || {}) };
                newData[key] = parsedValue;
            }
        } else if (property.type === 'object' && property.properties) {
            const nestedData = currentValue || {};
            const updatedNestedData = applyDefaultValues(property, nestedData);
            if (updatedNestedData !== nestedData) {
                if (!newData) newData = { ...(data || {}) };
                newData[key] = updatedNestedData;
            }
        }
    });
    return newData || data || {};
};


export const ADDRESS_BASE_SCHEMA = {
    "title": "Adresse",
    "type": "object",
    "properties": {
        "name": { "type": "string", "title": "Name" },
        "streetAddress": { "type": "string", "title": "Straße" },
        "postOfficeBox": { "type": "string", "title": "Postfach" },
        "extendedAddress": { "type": "string", "title": "Adresszusatz" },
        "postalCode": { "type": "string", "title": "PLZ" },
        "locality": { "type": "string", "title": "Stadt" },
        "countryName": { "type": "string", "title": "Land" }
    }
};


export const injectCompletions = (schema, completions) => {
    if (!schema || !schema.properties) return schema;
    const newProperties = { ...schema.properties };
    Object.keys(newProperties).forEach(key => {
        if (completions[key] && completions[key].length > 0) {
            newProperties[key] = {
                ...newProperties[key],
                examples: completions[key]
            };
        }
        // Recurse for nested objects
        if (newProperties[key] && newProperties[key].type === 'object' && newProperties[key].properties) {
            newProperties[key] = injectCompletions(newProperties[key], completions);
        }
    });
    return { ...schema, properties: newProperties };
};

export const generateUiSchema = (schema, completions, baseUiSchema = {}, options = {}) => {
    const uiSchema = { ...baseUiSchema };
    if (!schema) return uiSchema;

    if (schema["ui:order"]) {
        uiSchema["ui:order"] = schema["ui:order"];
    }

    if (!schema.properties) return uiSchema;

    const { useDateCombobox = false, showDynamicDateHint = false } = options;

    Object.keys(schema.properties).forEach(key => {
        const property = schema.properties[key];
        const propertyCompletions = completions[key];
        const isDateField = property.format === "date" || property["ui:options"]?.inputType === "date";
        const isBodyField = key.toLowerCase() === 'body';
        const isTextAreaField = key.toLowerCase().includes('text') || key.toLowerCase().includes('memo');

        // Add help text from description if present
        if (property.description) {
            uiSchema[key] = uiSchema[key] || {};
            uiSchema[key]["ui:help"] = (
                <small className="text-muted d-block mt-1">
                    {property.description}
                </small>
            );
        }

        if (isDateField) {
            const dateHelp = showDynamicDateHint ? (
                <small className="text-muted d-block mt-1">
                    Verwenden Sie "today", "now" oder "yesterday" für dynamische Daten.
                </small>
            ) : null;
            const completionsAndDates = [...(propertyCompletions || []), ...(useDateCombobox ? ["yesterday", "today", "now"] : [])];

            uiSchema[key] = {
                ...uiSchema[key],
                ...( (useDateCombobox || (propertyCompletions && propertyCompletions.length > 0)) ? { "ui:widget": "datalist" } : {}),
                "ui:options": {
                    ...(uiSchema[key]?.["ui:options"] || {}),
                    "autocomplete": "off",
                    ...(completionsAndDates.length > 0 ? { "list": completionsAndDates } : {})
                },
                "ui:help": uiSchema[key]?.["ui:help"]
                    ? (dateHelp ? <>{uiSchema[key]["ui:help"]}{dateHelp}</> : uiSchema[key]["ui:help"])
                    : (dateHelp || undefined)
            };
        } else if (isBodyField) {
            const templateSuffix = property["x-template-suffix"];

            if (templateSuffix === '.html') {
                uiSchema[key] = {
                    ...uiSchema[key],
                    "ui:widget": "html"
                };
            } else if (templateSuffix === '.tex' || templateSuffix === '.lco') {
                uiSchema[key] = {
                    ...uiSchema[key],
                    "ui:widget": "monaco",
                    "ui:options": {
                        ...(uiSchema[key]?.["ui:options"] || {}),
                        "language": "latex"
                    }
                };
            } else {
                uiSchema[key] = {
                    ...uiSchema[key],
                    "ui:widget": "textarea"
                };
            }
        } else if (isTextAreaField) {
            uiSchema[key] = {
                ...uiSchema[key],
                "ui:widget": "textarea"
            };
        } else if (propertyCompletions && propertyCompletions.length > 0) {
            uiSchema[key] = {
                ...uiSchema[key],
                "ui:widget": "datalist",
                "ui:options": {
                    ...(uiSchema[key]?.["ui:options"] || {}),
                    "list": propertyCompletions
                }
            };
            console.log(`Applying completions for ${key}:`, propertyCompletions);
        }

        if (property.type === 'object' && property.properties) {
            uiSchema[key] = generateUiSchema(property, completions, uiSchema[key], options);
        }
    });
    return uiSchema;
};



const isSameAddress = (a, b) => {
    if (!a || !b) return false;
    const fields = ['name', 'streetAddress', 'locality', 'postalCode', 'countryName', 'postOfficeBox', 'extendedAddress'];
    return fields.every(field => (a[field] || "") === (b[field] || ""));
};


export const IntermediateEditorModal = ({show, onHide, content, onSave, language, showNotification}) => {
    const [localContent, setLocalContent] = useState(content);

    useEffect(() => {
        if (show) {
            setLocalContent(content);
        }
    }, [content, show]);

    const handleSave = () => {
        onSave(localContent);
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" animation={false}>
            <Modal.Header closeButton>
                <Modal.Title>Zwischenschritt bearbeiten (Touch-up)</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: 0, backgroundColor: '#1e1e1e' }}>
                <Editor
                    height="75vh"
                    theme="vs-dark"
                    language={language}
                    value={localContent}
                    onChange={(value) => setLocalContent(value || "")}
                    options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        scrollBeyondLastLine: false,
                        wordWrap: "on",
                        automaticLayout: true,
                        tabSize: 4,
                        padding: { top: 10, bottom: 10 }
                    }}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Abbrechen
                </Button>
                <Button variant="primary" onClick={handleSave}>
                    Speichern & PDF neu erstellen
                </Button>
            </Modal.Footer>
        </Modal>
    );
};


export const RecordEditor = ({pk, schema, onHide, headline, json_instance, json_schema: initial_json_schema, default_from_address, default_to_address, default_data, url, recordfile_set, refreshData, showNotification, is_building: initial_is_building, intermediate_content: initial_intermediate_content, status: initial_status}) => {
    const [addresses, setAddresses] = useState([]);
    const [showAddressManager, setShowAddressManager] = useState(false);
    const [formData, setFormData] = useState(json_instance);
    const [fromData, setFromData] = useState(json_instance?.from_address || {});
    const [toData, setToData] = useState(json_instance?.to_address || {});
    const [completions, setCompletions] = useState({});
    const [localRecordfileSet, setLocalRecordfileSet] = useState(recordfile_set);
    const [isBuilding, setIsBuilding] = useState(initial_is_building || false);
    const [intermediateContent, setIntermediateContent] = useState(initial_intermediate_content || "");
    const [showIntermediateEditor, setShowIntermediateEditor] = useState(false);
    const [status, setStatus] = useState(initial_status !== undefined ? initial_status : 0);
    const pdfFile = useMemo(() => localRecordfileSet?.find(f => f.mimetype === 'application/pdf' || f.basename?.toLowerCase().endsWith('.pdf')), [localRecordfileSet]);

    const handleStatusChange = (newStatus) => {
        setStatus(newStatus);
        const payload = {
            "status": newStatus,
        };
        patchDrfRequest(url, payload, (response) => {
            console.log("Status updated successfully", response);
            if (showNotification) showNotification('Status aktualisiert');
            if (refreshData) refreshData();
        }, (err) => {
            if (showNotification) showNotification('Fehler beim Aktualisieren des Status', 'danger');
        });
    };

    useEffect(() => {
        setIntermediateContent(initial_intermediate_content || "");
    }, [initial_intermediate_content]);

    useEffect(() => {
        setIsBuilding(initial_is_building || false);
    }, [initial_is_building]);

    useEffect(() => {
        let interval;
        if (isBuilding) {
            console.log("Record is building, starting poll...");
            interval = setInterval(() => {
                getDrfRequest(url, (data) => {
                    if (!data.is_building) {
                        console.log("Building finished!");
                        setIsBuilding(false);
                        clearInterval(interval);
                        if (showNotification) showNotification('Kompilierung abgeschlossen', 'success');
                        if (refreshData) refreshData();
                        if (data.recordfile_set) {
                            setLocalRecordfileSet(data.recordfile_set);
                        }
                    }
                }, (err) => {
                    console.error("Polling error:", err);
                    setIsBuilding(false);
                    clearInterval(interval);
                });
            }, 2000);
        }
        return () => clearInterval(interval);
    }, [isBuilding, url]);

    useEffect(() => {
        setLocalRecordfileSet(recordfile_set);
    }, [recordfile_set]);

    useEffect(() => {
        console.log("RecordEditor mounting, fetching completions and data...");

        // Merge schema-wide default_data with json_instance if json_instance is empty
        const instanceIsEmpty = !json_instance || object_is_empty(json_instance);
        let baseData = json_instance || {};
        if (instanceIsEmpty && default_data && !object_is_empty(default_data)) {
            console.log("Applying schema-wide default data", default_data);
            baseData = { ...default_data, ...baseData };
        }

        const currentFormData = applyDefaultValues(initial_json_schema, baseData);
        const currentFromData = applyDefaultValues(ADDRESS_BASE_SCHEMA, baseData.from_address || {});
        const currentToData = applyDefaultValues(ADDRESS_BASE_SCHEMA, baseData.to_address || {});

        setFormData(currentFormData);
        setFromData(currentFromData);
        setToData(currentToData);

        getDrfRequest(buildUrl("/api/addresses/"), (data) => {
            setAddresses(data);

            // Auto-apply standard addresses if current fields are empty
            // Using functional updates to ensure we have the latest state
            setFromData(currentFrom => {
                const fromIsEmpty = !currentFrom || object_is_empty(currentFrom) || !currentFrom.name;
                if (fromIsEmpty) {
                    let stdFrom;
                    if (default_from_address) {
                        stdFrom = data.find(a => String(a.pk) === String(default_from_address));
                    }
                    // Fallback to first address if no default set or default not found
                    if (!stdFrom && data.length > 0) {
                        stdFrom = data[0];
                    }

                    if (stdFrom) {
                        console.log(`Applying default from address:`, stdFrom.data.name);
                        return stdFrom.data;
                    }
                }
                return currentFrom;
            });

            setToData(currentTo => {
                const toIsEmpty = !currentTo || object_is_empty(currentTo) || !currentTo.name;
                if (toIsEmpty) {
                    let stdTo;
                    if (default_to_address) {
                        stdTo = data.find(a => String(a.pk) === String(default_to_address));
                    }
                    // Fallback to first address if no default set or default not found
                    if (!stdTo && data.length > 0) {
                        stdTo = data[0];
                    }

                    if (stdTo) {
                        console.log(`Applying default to address:`, stdTo.data.name);
                        return stdTo.data;
                    }
                }
                return currentTo;
            });
        }, (err) => {
            console.error("Error fetching addresses:", err);
        });
        getDrfRequest(buildUrl("/api/users/completions/"), (data) => {
            console.log("Completions fetched:", data);
            setCompletions(data);
        }, (err) => {
            console.error("Error fetching completions:", err);
        });
    }, [pk, json_instance, default_from_address, default_to_address, default_data]);



    const json_schema = useMemo(() => injectCompletions(initial_json_schema, completions), [initial_json_schema, completions]);
    const contentUiSchema = useMemo(() => generateUiSchema(json_schema, completions, {
        "ui:submitButtonOptions": {
            "norender": true,
        }
    }), [json_schema, completions]);

    const applyAddress = (addressData, type) => {
        if (type === 'from') {
            setFromData(addressData);
        } else if (type === 'to') {
            setToData(addressData);
        }
    };

    const ADDRESS_SCHEMA = useMemo(() => injectCompletions(ADDRESS_BASE_SCHEMA, completions), [completions]);
    const addressUiSchema = useMemo(() => generateUiSchema(ADDRESS_SCHEMA, completions, {
        "ui:submitButtonOptions": {
            "norender": true,
        }
    }), [ADDRESS_SCHEMA, completions]);

    const saveAddress = (addressData) => {
        if (!addressData.name) {
            if (showNotification) showNotification('Name ist erforderlich, um eine Adresse zu speichern', 'danger');
            return;
        }
        const payload = { data: addressData };
        postDrfRequest(buildUrl("/api/addresses/"), payload, (response) => {
            if (showNotification) showNotification('Adresse im Adressbuch gespeichert');
            getDrfRequest(buildUrl("/api/addresses/"), (data) => {
                setAddresses(data);
            });
        }, (err) => {
            if (showNotification) showNotification('Fehler beim Speichern der Adresse', 'danger');
        });
    };

    const handleSave = () => {
        const payload = {
            "json_instance": {
                ...formData,
                "from_address": fromData,
                "to_address": toData
            },
            "status": status,
        };
        patchDrfRequest(url, payload, (response) => {
            console.log("Record saved successfully", response);
            if (showNotification) showNotification('Datensatz gespeichert. Kompilierung läuft...');
            if (response.is_building) {
                setIsBuilding(true);
            }
            if (refreshData) refreshData();
            if (response.recordfile_set) {
                setLocalRecordfileSet(response.recordfile_set);
            }
            if (response.intermediate_content !== undefined) {
                setIntermediateContent(response.intermediate_content);
            }
        }, (err) => {
            if (showNotification) showNotification('Fehler beim Speichern des Datensatzes', 'danger');
        });
    };

    const handleTouchup = () => {
        if (!intermediateContent) {
            if (showNotification) showNotification('Generiere Zwischenschritt...');
            postDrfRequest(`${url}render/`, {}, (data) => {
                setIntermediateContent(data.intermediate_content);
                setShowIntermediateEditor(true);
            }, (err) => {
                if (showNotification) showNotification('Fehler beim Generieren des Zwischenschritts', 'danger');
            });
        } else {
            setShowIntermediateEditor(true);
        }
    };

    const handleSaveIntermediate = (newContent) => {
        const payload = {
            intermediate_content: newContent
        };
        patchDrfRequest(url, payload, (response) => {
            if (showNotification) showNotification('Zwischenschritt gespeichert. PDF wird neu erstellt...');
            setIntermediateContent(response.intermediate_content);
            setShowIntermediateEditor(false);
            if (response.is_building) {
                setIsBuilding(true);
            }
            if (refreshData) refreshData();
        }, (err) => {
            if (showNotification) showNotification('Fehler beim Speichern des Zwischenschritts', 'danger');
        });
    };

    const getIntermediateLanguage = (content) => {
        if (!content) return 'html';
        if (content.includes('\\documentclass') || content.includes('\\begin{document}')) return 'latex';
        return 'html';
    };

    const handleSetAsDefault = () => {
        if (!schema) {
            if (showNotification) showNotification('Keine Vorlagen-ID gefunden', 'danger');
            return;
        }
        const schemaUrl = buildUrl(`/api/schemas/${schema}/`);

        const fromAddr = addresses.find(a => isSameAddress(a.data, fromData));
        const toAddr = addresses.find(a => isSameAddress(a.data, toData));

        const payload = {
            default_data: formData,
        };

        if (fromAddr) payload.default_from_address = fromAddr.pk;
        if (toAddr) payload.default_to_address = toAddr.pk;

        patchDrfRequest(schemaUrl, payload, (response) => {
            if (showNotification) showNotification('Werte erfolgreich in Vorlage übernommen');
            if (refreshData) refreshData();
        }, (err) => {
            if (showNotification) showNotification('Fehler beim Übernehmen in Vorlage', 'danger');
        });
    };

    return (
        <>
        <div className="record-editor py-4 overflow-visible">
                <div className="mb-4 d-flex justify-content-between align-items-center">
                    <div>
                        <small className="text-muted fw-bold text-uppercase d-block mb-2">Status</small>
                        <ButtonGroup size="sm">
                            <Button 
                                variant={status === 0 ? "secondary" : "outline-secondary"}
                                onClick={() => handleStatusChange(0)}
                            >
                                Draft
                            </Button>
                            <Button 
                                variant={status === 1 ? "success" : "outline-success"}
                                onClick={() => handleStatusChange(1)}
                            >
                                Final
                            </Button>
                        </ButtonGroup>
                    </div>
                </div>
                <div className="mb-4 pb-4 border-bottom position-relative overflow-visible">
                    <Button
                        variant="link"
                        className="p-0 text-green position-absolute top-0 end-0 m-2"
                        onClick={() => setShowAddressManager(true)}
                        title="Adressbuch verwalten"
                        style={{ zIndex: 10 }}
                    >
                        <JournalBookmarkFill size={18} />
                    </Button>
                    <Row className="overflow-visible g-4">
                        <Col xs={12} md={6}>
                            <Accordion flush>
                                <Accordion.Item eventKey="0" className="border-0">
                                    <Accordion.Header className="px-0 border-0 bg-transparent">
                                        <small className="text-muted fw-bold">ABSENDER</small>
                                        {fromData?.name && <small className="text-green ms-2">({fromData.name})</small>}
                                    </Accordion.Header>
                                    <Accordion.Body className="px-0">
                                        <div className="mb-3">
                                            <label className="small text-muted fw-bold text-uppercase mb-2">Schnellauswahl</label>
                                            <SimpleCombobox
                                                size="sm"
                                                value={addresses.find(a => isSameAddress(a.data, fromData))?.pk || ""}
                                                onChange={(val) => {
                                                    const addr = addresses.find(a => a.pk === parseInt(val));
                                                    if (addr) applyAddress(addr.data, 'from');
                                                }}
                                                list={addresses.map(addr => ({
                                                    pk: addr.pk,
                                                    label: `${addr.data.name || addr.data.streetAddress || `Adresse ${addr.pk}`} ${addr.data.locality ? `(${addr.data.locality})` : ''}`
                                                }))}
                                                placeholder="Absender wählen..."
                                            />
                                        </div>
                                        <Form
                                            schema={ADDRESS_SCHEMA}
                                            formData={fromData}
                                            onChange={({formData}) => setFromData(formData)}
                                            onSubmit={handleSave}
                                            validator={validator}
                                            children={true}
                                            widgets={{datalist: DatalistWidget, combobox: ComboboxWidget, monaco: MonacoWidget, html: HtmlWidget}}
                                            uiSchema={addressUiSchema}
                                            templates={{ButtonTemplates: {SubmitButton: JsonFormSubmitButton}}}
                                        />
                                        <div className="d-grid mt-2">
                                            <Button
                                                className="btn-outline-green"
                                                size="sm"
                                                onClick={() => saveAddress(fromData)}
                                                disabled={!fromData.name}
                                                title="Diese Adresse dauerhaft im Adressbuch speichern"
                                            >
                                                <FolderPlus className="me-1" /> Im Adressbuch speichern
                                            </Button>
                                        </div>
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        </Col>
                        <Col xs={12} md={6}>
                            <Accordion flush>
                                <Accordion.Item eventKey="0" className="border-0">
                                    <Accordion.Header className="px-0 border-0 bg-transparent">
                                        <small className="text-muted fw-bold">EMPFÄNGER</small>
                                        {toData?.name && <small className="text-green ms-2">({toData.name})</small>}
                                    </Accordion.Header>
                                    <Accordion.Body className="px-0">
                                        <div className="mb-3">
                                            <label className="small text-muted fw-bold text-uppercase mb-2">Schnellauswahl</label>
                                            <SimpleCombobox
                                                size="sm"
                                                value={addresses.find(a => isSameAddress(a.data, toData))?.pk || ""}
                                                onChange={(val) => {
                                                    const addr = addresses.find(a => a.pk === parseInt(val));
                                                    if (addr) applyAddress(addr.data, 'to');
                                                }}
                                                list={addresses.map(addr => ({
                                                    pk: addr.pk,
                                                    label: `${addr.data.name || addr.data.streetAddress || `Adresse ${addr.pk}`} ${addr.data.locality ? `(${addr.data.locality})` : ''}`
                                                }))}
                                                placeholder="Empfänger wählen..."
                                            />
                                        </div>
                                        <Form
                                            schema={ADDRESS_SCHEMA}
                                            formData={toData}
                                            onChange={({formData}) => setToData(formData)}
                                            onSubmit={handleSave}
                                            validator={validator}
                                            children={true}
                                            widgets={{datalist: DatalistWidget, combobox: ComboboxWidget, monaco: MonacoWidget, html: HtmlWidget}}
                                            uiSchema={addressUiSchema}
                                            templates={{ButtonTemplates: {SubmitButton: JsonFormSubmitButton}}}
                                        />
                                        <div className="d-grid mt-2">
                                            <Button
                                                className="btn-outline-green"
                                                size="sm"
                                                onClick={() => saveAddress(toData)}
                                                disabled={!toData.name}
                                                title="Diese Adresse dauerhaft im Adressbuch speichern"
                                            >
                                                <FolderPlus className="me-1" /> Im Adressbuch speichern
                                            </Button>
                                        </div>
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        </Col>
                    </Row>
                </div>

                <Row className="overflow-visible g-4">
                    <Col xs={12} md={12}>
                        <div className="h-100">
                    <div>
                        <Form
                            templates={{ButtonTemplates: {SubmitButton: JsonFormSubmitButton}}}
                            widgets={{datalist: DatalistWidget, combobox: ComboboxWidget, monaco: MonacoWidget, html: HtmlWidget}}
                            formData={formData}
                            onChange={({formData}) => setFormData(formData)}
                            onSubmit={handleSave}
                            schema={json_schema}
                            uiSchema={contentUiSchema}
                            validator={validator}
                        />
                    </div>
                </div>
            </Col>
            <Col xs={12} className="d-flex justify-content-between align-items-center mt-4 border-top pt-3">
                <div className="d-flex gap-3 align-items-center">
                    <DeleteButton
                        url={url}
                        refreshData={() => {
                            onHide();
                            refreshData();
                        }}
                        showNotification={showNotification}
                        size={24}
                        className="btn btn-outline-danger btn-sm"
                    />
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleTouchup}
                        title="Zwischenschritt bearbeiten (Touch-up)"
                    >
                        <PencilSquare size={18} />
                    </Button>
                    {pdfFile && (
                        <a
                            href={pdfFile.file}
                            className="btn btn-outline-primary btn-sm"
                            title="PDF herunterladen"
                            download={pdfFile.basename}
                        >
                            <Download className="me-1" />
                        </a>
                    )}
                </div>
                <div className="d-flex gap-2 align-items-center">
                    {isBuilding && (
                        <div className="d-flex align-items-center me-3 text-primary">
                            <ArrowClockwise className="spinner-border-anim me-2" />
                            <small className="fw-bold">Wird kompiliert...</small>
                        </div>
                    )}
                    <Button variant="secondary" onClick={onHide}>Abbrechen</Button>
                    {schema && (
                        <Button variant="outline-primary" onClick={handleSetAsDefault} title="In Vorlage übernehmen">
                            <Upload /> <span className="ms-1 d-md-down-none">In Vorlage übernehmen</span>
                        </Button>
                    )}
                    <Button className="btn-orange border-0" onClick={handleSave} disabled={isBuilding}>
                        {isBuilding ? 'Speichere...' : 'Speichern'}
                    </Button>
                </div>
            </Col>
            {localRecordfileSet?.length > 0 && (
                <Col xs={12} md={12}>
                    <FileList
                        file_set={localRecordfileSet}
                        refreshData={refreshData}
                        grid
                        show={true}
                        showNotification={showNotification}
                        fullWidth={true}
                        allowDelete={false}
                        hideFilename={true}
                    />
                </Col>
            )}
        </Row>
        </div>
        <IntermediateEditorModal
            show={showIntermediateEditor}
            onHide={() => setShowIntermediateEditor(false)}
            content={intermediateContent}
            onSave={handleSaveIntermediate}
            language={getIntermediateLanguage(intermediateContent)}
            showNotification={showNotification}
        />
        <Modal show={showAddressManager} onHide={() => setShowAddressManager(false)} size="xl" centered scrollable onClick={(e) => e.stopPropagation()} animation={false}>
            <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4">
                <Modal.Title className="h4 text-dark fw-bold d-flex align-items-center">
                    <JournalBookmarkFill size={32} className="me-3 text-green" />
                    Adressbuch verwalten
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <AddressManager
                    url={buildUrl("/api/addresses/")}
                    showNotification={showNotification}
                    onAddressesUpdate={() => getDrfRequest(buildUrl("/api/addresses/"), (data) => setAddresses(data))}
                />
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 pb-4 px-4">
                <Button variant="secondary" onClick={() => setShowAddressManager(false)}>Schließen</Button>
            </Modal.Footer>
        </Modal>
        </>
    );
};


export const CreateRecord = ({headline: schemaHeadline, schema, url, refreshData, showNotification}) => {
    const handleCreate = () => {
        const data = {
            "schema": schema,
        }
        postDrfRequest(url, data, (response) => {
            console.log('CREATE RECORD RESPONSE', response);
            if (showNotification) showNotification('Datensatz erfolgreich erstellt');
            if (refreshData) refreshData();
            window.dispatchEvent(new CustomEvent('record-created', { detail: response }));
        }, (err) => {
            if (showNotification) showNotification('Fehler beim Erstellen des Datensatzes', 'danger');
        })
    };

    return (
        <article className="card border-record border-0 bg-light h-100 overflow-visible cursor-pointer hover-border-record record-card"
                 onClick={handleCreate}>
            <div className="card-header bg-transparent border-0 pt-2 pb-2 px-3">
                <div className="d-flex align-items-center mb-0">
                    <FileEarmarkTextFill size={32} className="text-orange me-2" />
                    <div className="d-flex flex-column overflow-hidden">
                        <h6 className="card-title mb-0 text-truncate fw-normal bg-orange text-white px-2 py-1 rounded-1 d-inline-block" style={{maxWidth: '100%'}}>
                            Neuer Datensatz
                        </h6>
                        <div className="d-flex gap-2 text-muted mt-1" style={{fontSize: '0.65rem'}}>
                            <span>Klicken zum Erstellen</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="card-body p-0 d-flex align-items-center justify-content-center bg-white" style={{minHeight: '100px'}}>
                <PlusLg size={48} className="text-orange" />
            </div>
        </article>
    );
}

export const CreateAddress = ({onClick}) => {
    return (
        <article className="card shadow-green border-0 bg-light h-100 overflow-visible cursor-pointer hover-shadow-green address-card"
                 onClick={onClick}>
            <div className="card-header bg-transparent border-0 pt-2 pb-2 px-3">
                <div className="d-flex align-items-center mb-0">
                    <JournalBookmarkFill size={32} className="text-green me-2" />
                    <div className="d-flex flex-column overflow-hidden">
                        <h6 className="card-title mb-0 text-truncate fw-normal bg-green text-white px-2 py-1 rounded-1 d-inline-block" style={{maxWidth: '100%'}}>
                            Neue Adresse
                        </h6>
                        <div className="d-flex gap-2 text-muted mt-1" style={{fontSize: '0.65rem'}}>
                            <span>Klicken zum Erstellen</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="card-body p-0 d-flex align-items-center justify-content-center bg-white" style={{minHeight: '100px'}}>
                <PlusLg size={48} className="text-green" />
            </div>
        </article>
    );
}

export const UserNameEditor = ({initialName, url, showNotification}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [name, setName] = useState(initialName);
    const [tempName, setTempName] = useState(initialName);

    const handleSave = () => {
        if (tempName !== name) {
            patchDrfRequest(url, {name: tempName}, (response) => {
                console.log("User name updated successfully", response);
                showNotification('Name erfolgreich aktualisiert');
                setName(tempName);
                setIsEditing(false);
            }, (err) => {
                showNotification('Fehler beim Aktualisieren des Namens', 'danger');
                setTempName(name);
                setIsEditing(false);
            });
        } else {
            setIsEditing(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            setTempName(name);
            setIsEditing(false);
        }
    };

    if (isEditing) {
        return (
            <div className="d-flex align-items-center mb-3">
                <input
                    type="text"
                    className="form-control form-control-lg fw-bold border-schema shadow-sm w-auto"
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    style={{fontSize: '2.5rem'}}
                />
            </div>
        );
    }

    return (
        <h1 className="display-4 fw-bold mb-3 cursor-pointer d-inline-block"
            onClick={() => setIsEditing(true)}>
            {name}
        </h1>
    );
};


export const AddressManager = ({url, showNotification, userName, onAddressesUpdate}) => {
    const [addresses, setAddresses] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null);
    const [completions, setCompletions] = useState({});

    useEffect(() => {
        console.log("AddressManager mounting, fetching completions...");
        getDrfRequest(buildUrl("/api/users/completions/"), (data) => {
            console.log("AddressManager completions fetched:", data);
            setCompletions(data);
        }, (err) => {
            console.error("Error fetching completions:", err);
        });
    }, []);


    const ADDRESS_SCHEMA = injectCompletions({
        ...ADDRESS_BASE_SCHEMA,
        "properties": {
            ...ADDRESS_BASE_SCHEMA.properties,
            "name": { "type": "string", "title": "Name", "default": userName },
        },
        "required": ["locality", "countryName"]
    }, completions);


    const refreshData = () => {
        getDrfRequest(url, (data) => {
            setAddresses(data);
            if (onAddressesUpdate) onAddressesUpdate(data);
        }, (err) => {
            console.error(err);
            if (showNotification) showNotification('Fehler beim Laden der Adressen', 'danger');
        });
    };

    useEffect(() => {
        refreshData();
    }, []);

    const handleEdit = (address) => {
        setEditingAddress(address);
        setShowModal(true);
    };

    const handleCreate = () => {
        setEditingAddress(null);
        setShowModal(true);
    };

    const handleDelete = (pk) => {
        if (window.confirm('Möchten Sie diese Adresse wirklich löschen?')) {
            const cleanUrl = url.split('?')[0];
            const deleteUrl = cleanUrl.endsWith('/') ? `${cleanUrl}${pk}/` : `${cleanUrl}/${pk}/`;
            deleteDrfRequest(deleteUrl, () => {
                if (showNotification) showNotification('Adresse gelöscht');
                refreshData();
            }, (err) => {
                if (showNotification) showNotification('Fehler beim Löschen der Adresse', 'danger');
            });
        }
    };

    const onSubmit = ({formData}) => {
        const payload = {
            data: formData,
        };

        const cleanUrl = url.split('?')[0];
        if (editingAddress) {
            const patchUrl = cleanUrl.endsWith('/') ? `${cleanUrl}${editingAddress.pk}/` : `${cleanUrl}/${editingAddress.pk}/`;
            patchDrfRequest(patchUrl, payload, () => {
                if (showNotification) showNotification('Adresse aktualisiert');
                refreshData();
                setShowModal(false);
            }, (err) => {
                if (showNotification) showNotification('Fehler beim Aktualisieren', 'danger');
            });
        } else {
            postDrfRequest(url, payload, () => {
                if (showNotification) showNotification('Adresse erstellt');
                refreshData();
                setShowModal(false);
            }, (err) => {
                if (showNotification) showNotification('Fehler beim Erstellen', 'danger');
            });
        }
    };

    return (
        <div className="mt-4">
            <div className="d-flex flex-wrap gap-4 mb-4">
                <CreateAddress onClick={handleCreate} />
                {addresses.map((address) => (
                    <article
                        key={address.pk}
                        className="card shadow-green border-0 bg-light h-100 overflow-visible cursor-pointer hover-shadow-green address-card"
                        onClick={() => handleEdit(address)}
                    >
                        <div className="card-header bg-transparent border-0 pt-2 pb-2 px-3">
                            <div className="d-flex align-items-center mb-0">
                                <JournalBookmarkFill size={32} className="text-green me-2" />
                                <div className="d-flex flex-column overflow-hidden me-auto">
                                    <h6 className="card-title mb-0 text-truncate fw-normal bg-green text-white px-2 py-1 rounded-1 d-inline-block" style={{maxWidth: '100%'}}>
                                        {address.data.name || 'Adresse'}
                                    </h6>
                                </div>
                                <button className="btn btn-link btn-sm text-muted p-0 opacity-50 hover-opacity-100"
                                        onClick={(e) => { e.stopPropagation(); handleDelete(address.pk); }}>
                                    <Trash3Fill size={24} />
                                </button>
                            </div>
                        </div>
                        <div className="card-body p-3 bg-white" style={{minHeight: '100px', fontSize: '0.8rem'}}>
                            <div className="text-dark">
                                {address.data.streetAddress && <div className="text-truncate">{address.data.streetAddress}</div>}
                                {address.data.extendedAddress && <div className="text-truncate">{address.data.extendedAddress}</div>}
                                {address.data.postOfficeBox && <div className="text-truncate">Postfach {address.data.postOfficeBox}</div>}
                                <div className="text-truncate">{address.data.postalCode} {address.data.locality}</div>
                                <div className="text-muted small mt-1 text-truncate">{address.data.countryName}</div>
                            </div>
                        </div>
                    </article>
                ))}
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered animation={false}>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="h5 fw-bold">
                        {editingAddress ? 'Adresse bearbeiten' : 'Neue Adresse'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Adress-Typ removed as per requirement - use Schema Settings to set defaults */}
                    <Form
                        schema={ADDRESS_SCHEMA}
                        validator={validator}
                        formData={applyDefaultValues(ADDRESS_SCHEMA, editingAddress ? editingAddress.data : {})}
                        uiSchema={{
                            ...generateUiSchema(ADDRESS_SCHEMA, completions),
                            "ui:submitButtonOptions": {
                                "variant": "green"
                            }
                        }}
                        widgets={{datalist: DatalistWidget, combobox: ComboboxWidget, monaco: MonacoWidget, html: HtmlWidget}}
                        onSubmit={onSubmit}
                        templates={{ButtonTemplates: {SubmitButton: JsonFormSubmitButton}}}
                    />
                </Modal.Body>
            </Modal>
        </div>
    );
};


export const CreateSchema = ({headline: initialHeadline, url, refreshData, showNotification, parent, parentHeadline, compact = true}) => {
    const [headline, setHeadline] = useState(initialHeadline);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    useEffect(() => {
        if (!parent) {
            getDrfRequest(`${url}templates/`, (data) => {
                setTemplates(data);
                if (data.length > 0) {
                    const autoSelectedTemplate = data.find(t => t.name === "brief") || data.find(t => t.name === "briefvorlage") || data[0];
                    setSelectedTemplate(autoSelectedTemplate);
                    setHeadline(h => h || (autoSelectedTemplate.name.charAt(0).toUpperCase() + autoSelectedTemplate.name.slice(1)));
                }
            }, (err) => console.error(err));
        } else {
            const today = new Date().toLocaleDateString('de-DE');
            setHeadline(h => h || `Neue Vorlage vom ${today}`);
        }
    }, [url, parent, parentHeadline]);

    return (
        <Dropdown onSelect={(t) => {
            const template = JSON.parse(t);
            const oldDefaultHeadline = selectedTemplate ? selectedTemplate.name.charAt(0).toUpperCase() + selectedTemplate.name.slice(1) : "";
            setSelectedTemplate(template);
            if (!headline || headline === "" || headline === oldDefaultHeadline) {
                setHeadline(template.name.charAt(0).toUpperCase() + template.name.slice(1));
            }
        }} className={compact ? "" : "my-3"}>
            <InputGroup size={compact ? undefined : "lg"} className="shadow-sm rounded overflow-hidden" style={{maxWidth: compact ? '350px' : '600px'}}>
                <InputGroup.Text className="bg-light border-schema d-md-down-none">
                    <StackedSchemaIcon size={compact ? 20 : 32} className="text-primary" />
                </InputGroup.Text>
                {templates.length > 0 && !parent && (
                    <Dropdown.Toggle variant="outline-primary" size={compact ? undefined : "lg"} className="border-start-0 rounded-0 d-flex align-items-center border-md-down-start gap-2">
                        {selectedTemplate ? (
                            <>
                                {selectedTemplate.type === 'html' ? <FiletypeHtml size={18} /> : <FiletypeTxt size={18} />}
                                {selectedTemplate.name}
                            </>
                        ) : (compact ? "Basis" : "Basis wählen")}
                    </Dropdown.Toggle>
                )}
                <Button variant="primary" className={`${compact ? "px-2" : "px-4"} border-start-0 ${(!parent && templates.length > 0) ? "" : "border-md-down-start"}`} title={headline} onClick={() => {
                    const data = {
                        "headline": headline,
                        "parent": parent,
                        "template_name": selectedTemplate ? selectedTemplate.name : "",
                        "template_type": selectedTemplate ? selectedTemplate.type : ""
                    }
                    postDrfRequest(url, data, (response) => {
                        console.log('CREATE SCHEMA RESPONSE', response);
                        if (showNotification) showNotification('Vorlage erfolgreich erstellt');
                        if (refreshData) refreshData();
                        window.dispatchEvent(new CustomEvent('schema-created', { detail: response }));
                        if (!parent) {
                            const autoSelectedTemplate = templates.find(t => t.name === "brief") || (templates.length > 0 ? (templates.find(t => t.name === "briefvorlage") || templates[0]) : null);
                            if (autoSelectedTemplate) {
                                setHeadline(autoSelectedTemplate.name.charAt(0).toUpperCase() + autoSelectedTemplate.name.slice(1));
                                setSelectedTemplate(autoSelectedTemplate);
                            }
                        } else {
                            const today = new Date().toLocaleDateString('de-DE');
                            setHeadline(`Neue Vorlage vom ${today}`);
                        }
                    }, (err) => {
                        if (showNotification) showNotification('Fehler beim Erstellen der Vorlage', 'danger');
                    })
                }}>
                    <PlusLg size={compact ? 20 : 32}/>
                </Button>
            </InputGroup>

            {templates.length > 0 && !parent && (
                <Dropdown.Menu>
                    {templates.map(t => (
                        <Dropdown.Item key={`${t.type}-${t.name}`} eventKey={JSON.stringify(t)} className="d-flex align-items-center gap-2">
                            {t.type === 'html' ? <FiletypeHtml /> : <FiletypeTxt />}
                            {t.name}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            )}
        </Dropdown>
    );
}


export const SchemaEditor = ({ pk, onHide, headline, schema, url, defaultFrom, defaultTo, defaultData, schemafile_set, refreshData, showNotification }) => {
    const [tempHeadline, setTempHeadline] = useState(headline);
    const [tempDefaultFrom, setTempDefaultFrom] = useState(defaultFrom);
    const [tempDefaultTo, setTempDefaultTo] = useState(defaultTo);
    const [tempDefaultData, setTempDefaultData] = useState(defaultData || {});
    const [addresses, setAddresses] = useState([]);
    const [showAddressManager, setShowAddressManager] = useState(false);
    const [completions, setCompletions] = useState({});
    const [localSchemafileSet, setLocalSchemafileSet] = useState(schemafile_set || []);

    useEffect(() => {
        setLocalSchemafileSet(schemafile_set || []);
    }, [schemafile_set]);

    useEffect(() => {
        setTempHeadline(headline);
        setTempDefaultFrom(defaultFrom);
        setTempDefaultTo(defaultTo);

        const dataWithDefaults = { ...(defaultData || {}) };
        const fillDateDefaults = (sch, d) => {
            if (!sch || !sch.properties) return;
            Object.keys(sch.properties).forEach(key => {
                const property = sch.properties[key];
                if (property.format === 'date' || property["ui:options"]?.inputType === 'date') {
                    if (d[key] === undefined || d[key] === null || d[key] === "") {
                        d[key] = "yesterday";
                    }
                } else if (property.type === 'object' && property.properties) {
                    d[key] = d[key] || {};
                    fillDateDefaults(property, d[key]);
                }
            });
        };
        fillDateDefaults(schema, dataWithDefaults);
        setTempDefaultData(dataWithDefaults);
    }, [schema, headline, defaultFrom, defaultTo, defaultData]);

    useEffect(() => {
        getDrfRequest(buildUrl("/api/addresses/"), (data) => {
            setAddresses(data);
            if (data.length > 0) {
                setTempDefaultFrom(prev => prev || data[0].pk);
                setTempDefaultTo(prev => prev || data[0].pk);
            }
        }, (err) => console.error(err));
        getDrfRequest(buildUrl("/api/users/completions/"), (data) => {
            setCompletions(data);
        }, (err) => console.error(err));
    }, []);


    const handleSave = (callback = null) => {
        const payload = {
            headline: tempHeadline,
            default_from_address: tempDefaultFrom || null,
            default_to_address: tempDefaultTo || null,
            default_data: tempDefaultData
        };
        patchDrfRequest(url, payload, (response) => {
            console.log("Schema updated successfully", response);
            if (showNotification) showNotification('Vorlage-Einstellungen erfolgreich aktualisiert');
            if (refreshData) refreshData();
            if (callback && typeof callback === 'function') callback();
        }, (err) => {
            if (showNotification) showNotification('Fehler beim Aktualisieren der Einstellungen', 'danger');
        });
    };

    const handleSaveAndCreateRecord = () => {
        handleSave(() => {
            const data = {
                "schema": pk,
            }
            postDrfRequest(buildUrl("/api/records/"), data, (response) => {
                if (showNotification) showNotification('Datensatz erfolgreich erstellt');
                if (refreshData) refreshData();
                window.dispatchEvent(new CustomEvent('record-created', { detail: response }));
            }, (err) => {
                if (showNotification) showNotification('Fehler beim Erstellen des Datensatzes', 'danger');
            })
        });
    };


    const injectedSchema = useMemo(() => injectCompletions(schema, completions), [schema, completions]);
    const uiSchema = useMemo(() => generateUiSchema(schema, completions, {
        "ui:submitButtonOptions": {
            "norender": true,
        }
    }, { useDateCombobox: true, showDynamicDateHint: true }), [schema, completions]);

    return (
        <>
        <div className="schema-editor">
            <div className="d-flex align-items-center mb-4">
                    <GearFill size={32} className="text-primary me-3" />
                    <h2 className="mb-0 me-3">{headline}</h2>
                    <div className="flex-grow-1"></div>
                    <CreateSchema
                        parent={pk}
                        parentHeadline={headline}
                        url={buildUrl("/api/schemas/")}
                        refreshData={refreshData}
                        showNotification={showNotification}
                    />
                    <Button
                        className="btn-orange border-0 ms-2"
                        title="Speichern und neuen Datensatz erstellen"
                        onClick={handleSaveAndCreateRecord}
                    >
                        <PlusLg size={20} />
                    </Button>
            </div>
                <div className="mb-4 pb-4 border-bottom position-relative overflow-visible">
                    <div className="mb-3">
                        <label className="form-label small fw-bold text-muted mb-1">Titel der Vorlage</label>
                        <FormControl
                            type="text"
                            value={tempHeadline}
                            onChange={(e) => setTempHeadline(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                            placeholder="Titel der Vorlage"
                        />
                    </div>
                    <Button
                        variant="link"
                        className="p-0 text-green position-absolute top-0 end-0 m-2"
                        onClick={() => setShowAddressManager(true)}
                        title="Adressbuch verwalten"
                        style={{ zIndex: 10 }}
                    >
                        <JournalBookmarkFill size={18} />
                    </Button>
                    <Row className="g-3">
                        <Col md={6}>
                            <label className="form-label small fw-bold text-muted mb-1">Standard-Absender</label>
                            <SimpleCombobox
                                size="sm"
                                value={tempDefaultFrom || ''}
                                onChange={(val) => setTempDefaultFrom(val)}
                                list={addresses.map(addr => ({
                                        pk: addr.pk,
                                        label: `${addr.data.name || addr.data.streetAddress || `Adresse ${addr.pk}`} ${addr.data.locality ? `(${addr.data.locality})` : ''}`
                                    }))}
                                placeholder="Absender wählen..."
                            />
                        </Col>
                        <Col md={6}>
                            <label className="form-label small fw-bold text-muted mb-1">Standard-Empfänger</label>
                            <SimpleCombobox
                                size="sm"
                                value={tempDefaultTo || ''}
                                onChange={(val) => setTempDefaultTo(val)}
                                list={addresses.map(addr => ({
                                        pk: addr.pk,
                                        label: `${addr.data.name || addr.data.streetAddress || `Adresse ${addr.pk}`} ${addr.data.locality ? `(${addr.data.locality})` : ''}`
                                    }))}
                                placeholder="Empfänger wählen..."
                            />
                        </Col>
                    </Row>
                    <div className="form-text mt-2 small">
                        Wählen Sie die Adressen aus, die standardmäßig für Absender/Empfänger in dieser Vorlage verwendet werden soll.
                    </div>
                </div>

                <div className="mb-4 pb-4 border-bottom">
                    <h6 className="text-muted text-uppercase small fw-bold mb-3">Inhalt-Voreinstellungen</h6>
                    <Form
                        schema={injectedSchema}
                        formData={tempDefaultData}
                        onChange={(e) => setTempDefaultData(e.formData)}
                        onSubmit={handleSave}
                        validator={validator}
                        uiSchema={uiSchema}
                        widgets={{datalist: DatalistWidget, combobox: ComboboxWidget, monaco: MonacoWidget, html: HtmlWidget}}
                        templates={{ButtonTemplates: {SubmitButton: JsonFormSubmitButton}}}
                    />
                    <div className="form-text mt-2 small">
                        Geben Sie hier Werte ein, die standardmäßig beim Erstellen eines neuen Datensatzes vorausgefüllt werden sollen.
                    </div>
                </div>

                <div className="mb-4 pb-4 border-bottom">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="text-muted text-uppercase small fw-bold mb-0">Dateien</h6>
                        <a 
                            href={`${url}download_files_zip/`} 
                            className="btn btn-outline-primary btn-sm" 
                            title="Alle Vorlage-Dateien als ZIP herunterladen"
                            download
                        >
                            <Download /> <span className="ms-1 d-md-down-none">Vorlage-Dateien ZIP</span>
                        </a>
                    </div>
                    <FileList
                        file_set={localSchemafileSet}
                        refreshData={refreshData}
                        showNotification={showNotification}
                        uploadUrl={`${url}upload_file/`}
                        fullWidth={true}
                        show={true}
                    />
                    <div className="form-text mt-2 small">
                        Hier können Sie Vorlagen-Dateien (z.B. HTML oder LaTeX Templates) für diese Schema-Vorlage verwalten.
                    </div>
                </div>

            <div className="d-flex justify-content-between align-items-center mt-4 border-top pt-3">
                <div className="d-flex gap-3 align-items-center">
                    <DeleteButton 
                        url={url} 
                        refreshData={() => {
                            window.location.reload();
                        }} 
                        showNotification={showNotification} 
                        size={24}
                        className="btn btn-outline-danger btn-sm"
                    />
                    <a 
                        href={`${url}download_records_zip/`} 
                        className="btn btn-outline-primary btn-sm" 
                        title="Alle Dateien aller Datensätze als ZIP herunterladen"
                        download
                    >
                        <Download /> <span className="ms-1 d-md-down-none">Alle Datensätze ZIP</span>
                    </a>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="secondary" onClick={onHide}>Abbrechen</Button>
                    <Button variant="primary" onClick={() => handleSave()}>Speichern</Button>
                    <Button className="btn-orange border-0 d-flex align-items-center gap-2" onClick={handleSaveAndCreateRecord} title="Speichern und neuen Datensatz erstellen">
                        Speichern <PlusLg />
                    </Button>
                </div>
            </div>
        </div>
        <Modal show={showAddressManager} onHide={() => setShowAddressManager(false)} size="xl" centered scrollable onClick={(e) => e.stopPropagation()} animation={false}>
            <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4">
                <Modal.Title className="h4 text-dark fw-bold d-flex align-items-center">
                    <JournalBookmarkFill size={32} className="me-3 text-green" />
                    Adressbuch verwalten
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <AddressManager 
                    url={buildUrl("/api/addresses/")} 
                    showNotification={showNotification} 
                    onAddressesUpdate={() => getDrfRequest(buildUrl("/api/addresses/"), (data) => setAddresses(data))}
                />
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 pb-4 px-4">
                <Button variant="secondary" onClick={() => setShowAddressManager(false)}>Schließen</Button>
            </Modal.Footer>
        </Modal>
        </>
    );
};


export const ViewButton = (props) => {
    return (
        <a className={"btn btn-secondary btn-icon"} target="_blank" href={props.file}>
            <EyeFill size={32}/>
        </a>
    )
}

export const UploadFile = ({url, refreshData, showNotification, className}) => {
    return (
        <div className={className || "mb-0"}>
            <label className="btn btn-outline-secondary btn-sm">
                <PlusLg size={32} className=""/>
                <input type="file" multiple className="d-none" onChange={(event) => {
                    const selectedFiles = event.target.files;
                    if (!selectedFiles || selectedFiles.length === 0) return;

                    const urlObj = new URL(url, window.location.origin);
                    const params = new URLSearchParams(urlObj.search);

                    const uploadPromises = Array.from(selectedFiles).map(file => {
                        const formData = new FormData();
                        formData.append("file", file, file.name);
                        
                        // Append all query parameters from the URL to the FormData
                        params.forEach((value, key) => {
                            formData.append(key, value);
                        });

                        return new Promise((resolve, reject) => {
                            postFormDataRequest(url, formData, (data) => {
                                resolve(data);
                            }, (err) => {
                                reject(err);
                            });
                        });
                    });

                    Promise.all(uploadPromises)
                        .then((results) => {
                            console.log("All uploads successful", results);
                            if (showNotification) {
                                const message = results.length > 1
                                    ? `${results.length} Dateien erfolgreich hochgeladen`
                                    : 'Datei erfolgreich hochgeladen';
                                showNotification(message);
                            }
                            if (refreshData) refreshData();
                        })
                        .catch((err) => {
                            console.error("One or more uploads failed", err);
                            if (showNotification) showNotification('Fehler beim Hochladen einer oder mehrerer Dateien', 'danger');
                            if (refreshData) refreshData();
                        });
                }}/>
            </label>
        </div>
    );
};


export const DeleteButton = ({url, actually_delete, refreshData, showNotification, icon, size, className}) => {
    return (
        <button className={className || "btn btn-link btn-sm text-danger p-0 opacity-50 hover-opacity-100"}
                onClick={(event) => {
                    event.stopPropagation();
                    if (actually_delete) {
                        deleteDrfRequest(url, (response) => {
                            console.log('DELETE RESPONSE', response);
                            if (showNotification) showNotification('Erfolgreich gelöscht');
                            if (refreshData) refreshData();
                        }, (err) => {
                            if (showNotification) showNotification('Fehler beim Löschen', 'danger');
                        });
                    } else {
                        deleteDrfRequest(url, (response) => {
                            console.log('PATCH RESPONSE DELETED', response);
                            if (showNotification) showNotification('Erfolgreich gelöscht (markiert)');
                            if (refreshData) refreshData();
                        }, (err) => {
                            if (showNotification) showNotification('Fehler beim Löschen', 'danger');
                        });
                    }
                }}>
            {icon || <Trash3Fill size={size || 32}/>}
        </button>
    );
}


export const DuplicateButton = ({url, refreshData, showNotification}) => {
    return (
        <button className={"btn btn-success btn-icon"}
                title="Duplizieren"
                onClick={(event) => {
                    postDrfRequest(`${url}/duplicate`, {}, (response) => {
                        console.log('DUPLICATE RESPONSE', response);
                        if (showNotification) showNotification('Erfolgreich dupliziert');
                        if (refreshData) refreshData();
                    }, (err) => {
                        if (showNotification) showNotification('Fehler beim Duplizieren', 'danger');
                    });
                }
                }>
            <Files size={32}/>
        </button>
    );
}

export const UserProfileHeader = ({initialName, initialTelephone, initialEmail, onClick}) => {
    const [userData, setUserData] = useState({
        name: initialName || '',
        telephone: initialTelephone || '',
        email: initialEmail || ''
    });

    useEffect(() => {
        const handleUpdate = (e) => {
            const updated = e.detail;
            setUserData({
                name: updated.name,
                telephone: updated.telephone,
                email: updated.email
            });
        };
        window.addEventListener('user-updated', handleUpdate);
        return () => window.removeEventListener('user-updated', handleUpdate);
    }, []);

    return (
        <div className="d-flex align-items-center gap-2">
            <div className="text-end lh-1 cursor-pointer me-1" onClick={onClick}>
                <div className="fw-bold text-dark mb-1">{userData.name || 'Name festlegen'}</div>
                <div style={{fontSize: '0.75rem'}} className="text-muted">{userData.email}</div>
                {userData.telephone && <div style={{fontSize: '0.75rem'}} className="text-muted">{userData.telephone}</div>}
            </div>
        </div>
    );
};


export const ProfileSettings = ({ initialName, initialTelephone, initialEmail, url, showNotification }) => {
    const [userData, setUserData] = useState({
        name: initialName || '',
        telephone: initialTelephone || '',
        email: initialEmail || ''
    });

    const handleSave = (e) => {
        e.preventDefault();
        patchDrfRequest(url, userData, (response) => {
            if (showNotification) showNotification('Profil erfolgreich aktualisiert');
            setUserData({
                name: response.name,
                telephone: response.telephone,
                email: response.email
            });
            window.dispatchEvent(new CustomEvent('user-updated', { detail: response }));
        }, (err) => {
            if (showNotification) showNotification('Fehler beim Aktualisieren des Profils', 'danger');
        });
    };

    return (
        <form onSubmit={handleSave} className="py-2">
            <div className="mb-3">
                <label className="form-label small fw-bold text-muted mb-1">Name</label>
                <input 
                    type="text" 
                    className="form-control" 
                    value={userData.name} 
                    onChange={e => setUserData({...userData, name: e.target.value})} 
                />
            </div>
            <div className="mb-3">
                <label className="form-label small fw-bold text-muted mb-1">Telefon</label>
                <input 
                    type="text" 
                    className="form-control" 
                    value={userData.telephone} 
                    onChange={e => setUserData({...userData, telephone: e.target.value})} 
                />
            </div>
            <div className="mb-3">
                <label className="form-label small fw-bold text-muted mb-1">E-Mail</label>
                <input 
                    type="email" 
                    className="form-control" 
                    value={userData.email} 
                    onChange={e => setUserData({...userData, email: e.target.value})} 
                />
            </div>
            <div className="d-flex justify-content-end">
                <Button variant="primary" type="submit">Speichern</Button>
            </div>
        </form>
    );
};


export const GlobalSettings = ({ addressUrl, schemaUrl, userUrl, initialName, initialTelephone, initialEmail, showNotification, onHide }) => {
    const restoreInputRef = useRef(null);

    const handleRestore = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        if (window.confirm("Möchten Sie das Backup wirklich einspielen? Alle aktuellen Daten werden beibehalten, aber Duplikate können entstehen.")) {
            postFormDataRequest("/api/backup/upload/", formData, (response) => {
                showNotification("Backup erfolgreich eingespielt. Seite wird neu geladen...");
                setTimeout(() => window.location.reload(), 2000);
            }, (err) => {
                showNotification("Fehler beim Einspielen des Backups", "danger");
            });
        }
        // Clear input
        event.target.value = '';
    };

    return (
        <div className="global-settings py-4">
            <div className="d-flex align-items-center mb-4">
                <GearFill size={32} className="text-primary me-3" />
                <h2 className="mb-0">Einstellungen</h2>
            </div>

            <Accordion defaultActiveKey="0" className="mb-4">
                <Accordion.Item eventKey="0" className="border-0 mb-3">
                    <Accordion.Header className="rounded border-bottom">
                        <div className="d-flex align-items-center gap-2">
                            <PersonFill className="text-primary" size={20} />
                            <span className="fw-bold">Profil-Informationen</span>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body className="bg-light-subtle">
                        <ProfileSettings 
                            url={userUrl} 
                            initialName={initialName} 
                            initialTelephone={initialTelephone} 
                            initialEmail={initialEmail} 
                            showNotification={showNotification} 
                        />
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1" className="border-0 mb-3">
                    <Accordion.Header className="rounded border-bottom">
                        <div className="d-flex align-items-center gap-2">
                            <JournalBookmarkFill className="text-green" size={20} />
                            <span className="fw-bold">Adressbuch verwalten</span>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body className="bg-light-subtle">
                        <AddressManager url={addressUrl} showNotification={showNotification} />
                    </Accordion.Body>
                </Accordion.Item>
                
                <Accordion.Item eventKey="2" className="border-0 mb-3">
                    <Accordion.Header className="rounded border-bottom">
                        <div className="d-flex align-items-center gap-2">
                            <PlusLg className="text-primary" size={20} />
                            <span className="fw-bold">Neue Vorlage erstellen</span>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body className="bg-light-subtle">
                        <div className="d-flex align-items-center gap-3 py-2">
                            <span className="text-muted">Wählen Sie eine Basisvorlage aus:</span>
                            <CreateSchema
                                parent={null}
                                url={schemaUrl}
                                showNotification={showNotification}
                            />
                        </div>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="3" className="border-0 mb-3">
                    <Accordion.Header className="rounded border-bottom">
                        <div className="d-flex align-items-center gap-2">
                            <ArrowCounterclockwise className="text-orange" size={20} />
                            <span className="fw-bold">Backup & Wiederherstellung</span>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body className="bg-light-subtle">
                        <div className="d-flex flex-column flex-md-row gap-3 py-2">
                            <a href="/api/backup/download/" className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2 px-4">
                                <Download /> Backup herunterladen
                            </a>
                            <Button variant="outline-primary" className="d-flex align-items-center justify-content-center gap-2 px-4" onClick={() => restoreInputRef.current?.click()}>
                                <Upload /> Backup einspielen
                            </Button>
                            <input 
                                type="file" 
                                ref={restoreInputRef} 
                                style={{ display: 'none' }} 
                                onChange={handleRestore}
                                accept=".zip"
                            />
                        </div>
                        <small className="text-muted d-block mt-2">
                            Ein Backup enthält alle Vorlagen, Datensätze und Adressen.
                        </small>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
            
            <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                <Button variant="secondary" onClick={onHide}>Schließen</Button>
            </div>
        </div>
    );
};
